/**
 * BrokerOrderLogPage
 *
 * Shows recent live broker orders for the logged-in user.
 * Paginated table with status badges.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { brokerApi } from '../../api/brokerClient';
import { resolveOrderLogStatus, resolveOrderLogReason, orderLogStatusBadgeClass } from './orderLogDisplay';

function StatusBadge({ status }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${orderLogStatusBadgeClass(status)}`}>
      {status}
    </span>
  );
}

export default function BrokerOrderLogPage() {
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['brokerOrderLog', offset],
    queryFn: () => brokerApi.getOrderLog({ limit, offset }),
  });

  const items = data?.items || [];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-txt-primary">Broker Order Log</h1>
          <p className="text-sm text-txt-secondary mt-0.5">Live orders placed from strategy signals</p>
        </div>
      </div>

      {isLoading && (
        <div className="animate-pulse space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-10 bg-bg-hover rounded" />
          ))}
        </div>
      )}

      {error && (
        <div className="bg-bear/10 text-bear text-sm px-4 py-3 rounded-lg border border-bear/30">
          Failed to load order log.
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="text-center text-txt-muted py-16 text-sm">
          No orders yet. Deploy a strategy with live broker enabled to see orders here.
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-bg-border">
          <table className="w-full text-sm">
            <thead className="bg-bg-surface text-xs text-txt-label uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Time</th>
                <th className="px-4 py-3 text-left">Broker</th>
                <th className="px-4 py-3 text-left">Symbol</th>
                <th className="px-4 py-3 text-left">Side</th>
                <th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Order ID</th>
                <th className="px-4 py-3 text-left">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-bg-border">
              {items.map((row) => (
                <tr key={row.id} className="hover:bg-bg-hover transition-colors">
                  <td className="px-4 py-3 text-txt-secondary whitespace-nowrap font-mono text-xs">
                    {row.placed_at ? new Date(row.placed_at).toLocaleString('en-IN', {
                      timeZone: 'Asia/Kolkata',
                      hour12: false,
                    }) : '—'}
                  </td>
                  <td className="px-4 py-3 font-medium text-txt-primary">{row.broker_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-txt-primary">{row.symbol || '—'}</td>
                  <td className="px-4 py-3">
                    {row.side && (
                      <span className={`text-xs font-semibold ${
                        row.side === 'BUY' ? 'text-bull' : 'text-bear'
                      }`}>
                        {row.side}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-txt-primary">{row.quantity ?? '—'}</td>
                  <td className="px-4 py-3"><StatusBadge status={resolveOrderLogStatus(row)} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-txt-secondary">
                    {row.broker_order_id || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-bear max-w-xs truncate" title={resolveOrderLogReason(row)}>
                    {resolveOrderLogReason(row) || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {items.length > 0 && (
        <div className="flex justify-between items-center mt-4 text-sm text-txt-secondary">
          <span>Showing {offset + 1}–{offset + items.length}</span>
          <div className="flex gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              className="px-3 py-1 border border-bg-border rounded disabled:opacity-40 hover:bg-bg-hover transition-colors"
            >
              Previous
            </button>
            <button
              disabled={items.length < limit}
              onClick={() => setOffset(offset + limit)}
              className="px-3 py-1 border border-bg-border rounded disabled:opacity-40 hover:bg-bg-hover transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
